import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useEffect, useRef } from 'react';
import { SectionContainer, Container, SectionHeader, SectionTitle, Form, FormTitle, FormGroup, Label, Input, Textarea, Button, SuccessMessage, ErrorMessage, SocialIconsContainer, SocialIconLink, SocialIcon, } from './Contact.styles';
import { useAnalytics } from '../../../hooks/useAnalytics';
import linkedinIcon from '../../../assets/linkedin.svg';
import githubIcon from '../../../assets/github.svg';
import instagramIcon from '../../../assets/instagram.svg';
// Social media links - Update these with your actual profile URLs
const SOCIAL_LINKS = {
    linkedin: 'https://www.linkedin.com/in/raja-kedia-37844a105/', // Update with your LinkedIn URL
    github: 'https://github.com/rajakedia1', // Update with your GitHub URL
    instagram: 'https://www.instagram.com/_coder_became_wanderer/', // Update with your Instagram URL
};
const Contact = () => {
    const { trackSectionView, trackContactForm, trackExternalLink } = useAnalytics();
    const sectionRef = useRef(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
    });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSubmitted, setIsSubmitted] = useState(false);
    // Track section view when it comes into viewport
    useEffect(() => {
        if (typeof window === 'undefined' || !sectionRef.current)
            return;
        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting) {
                    trackSectionView('contact');
                    trackContactForm('view');
                    observer.disconnect();
                }
            });
        }, { threshold: 0.3 });
        observer.observe(sectionRef.current);
        return () => observer.disconnect();
    }, [trackSectionView, trackContactForm]);
    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = 'Name is required';
        }
        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        }
        else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }
        if (!formData.message.trim()) {
            newErrors.message = 'Message is required';
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        // Clear error when user starts typing
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) {
            return;
        }
        setIsSubmitting(true);
        try {
            // Environment-aware API endpoint
            // Development: Uses Express server (localhost:3001) via webpack proxy
            // Production: Uses Firebase Functions
            const apiUrl = process.env.NODE_ENV === 'development'
                ? '/api/contact' // Webpack proxy routes to localhost:3001
                : 'https://us-central1-raja-portfolio-dad3c.cloudfunctions.net/api/contact'; // Direct Functions URL
            const response = await fetch(apiUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(formData),
            });
            const data = await response.json();
            if (response.ok && data.success) {
                setIsSubmitted(true);
                setFormData({ name: '', email: '', message: '' });
                trackContactForm('submit');
                setTimeout(() => setIsSubmitted(false), 5000); // Hide success message after 5 seconds
            }
            else {
                throw new Error(data.error || 'Failed to send message');
            }
        }
        catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = error instanceof Error ? error.message : 'Unknown error';
            trackContactForm('error', errorMessage);
            alert('Failed to send message. Please try again.');
        }
        finally {
            setIsSubmitting(false);
        }
    };
    return (_jsx(SectionContainer, { id: "contact", ref: sectionRef, children: _jsxs(Container, { children: [_jsx(SectionHeader, { children: _jsx(SectionTitle, { children: "Get In Touch" }) }), _jsxs(Form, { onSubmit: handleSubmit, children: [_jsx(FormTitle, { children: "Let's build together." }), isSubmitted && (_jsx(SuccessMessage, { children: "\u2713 Thank you! Your message has been sent successfully." })), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "name", children: "Name" }), _jsx(Input, { type: "text", id: "name", name: "name", placeholder: "Your name", value: formData.name, onChange: handleChange, "$hasError": !!errors.name, required: true }), errors.name && _jsx(ErrorMessage, { children: errors.name })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "email", children: "Email" }), _jsx(Input, { type: "email", id: "email", name: "email", placeholder: "your@email.com", value: formData.email, onChange: handleChange, "$hasError": !!errors.email, required: true }), errors.email && _jsx(ErrorMessage, { children: errors.email })] }), _jsxs(FormGroup, { children: [_jsx(Label, { htmlFor: "message", children: "Message" }), _jsx(Textarea, { id: "message", name: "message", rows: 5, placeholder: "Tell me about your project...", value: formData.message, onChange: handleChange, "$hasError": !!errors.message, required: true }), errors.message && _jsx(ErrorMessage, { children: errors.message })] }), _jsx(Button, { type: "submit", "$isSubmitting": isSubmitting, disabled: isSubmitting, children: isSubmitting ? 'Sending...' : 'Send Message' })] }), _jsxs(SocialIconsContainer, { children: [_jsx(SocialIconLink, { href: SOCIAL_LINKS.linkedin, target: "_blank", rel: "noopener noreferrer", "aria-label": "LinkedIn", onClick: () => trackExternalLink(SOCIAL_LINKS.linkedin, 'LinkedIn'), children: _jsx(SocialIcon, { src: linkedinIcon, alt: "LinkedIn" }) }), _jsx(SocialIconLink, { href: SOCIAL_LINKS.github, target: "_blank", rel: "noopener noreferrer", "aria-label": "GitHub", onClick: () => trackExternalLink(SOCIAL_LINKS.github, 'GitHub'), children: _jsx(SocialIcon, { src: githubIcon, alt: "GitHub" }) }), _jsx(SocialIconLink, { href: SOCIAL_LINKS.instagram, target: "_blank", rel: "noopener noreferrer", "aria-label": "Instagram", onClick: () => trackExternalLink(SOCIAL_LINKS.instagram, 'Instagram'), children: _jsx(SocialIcon, { src: instagramIcon, alt: "Instagram" }) })] })] }) }));
};
export default Contact;
